import React, { useEffect, useState } from 'react';
import {
  Card,
  Form,
  Input,
  Button,
  message,
  Typography,
  Alert,
  Space,
  Divider,
  Tag,
} from 'antd';
import { SaveOutlined, LinkOutlined, InfoCircleOutlined } from '@ant-design/icons';
import api from '../../services/api';

const { Title, Text, Paragraph } = Typography;

interface SystemConfig {
  odoo_url: string;
  odoo_token_expiry_minutes: number;
  updated_at: string | null;
  updated_by: string | null;
}

const SystemConfigPage: React.FC = () => {
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [config, setConfig] = useState<SystemConfig | null>(null);
  const [form] = Form.useForm();

  useEffect(() => {
    fetchSystemConfig();
  }, []);

  const fetchSystemConfig = async () => {
    try {
      setLoading(true);
      const response = await api.get('/api/super-admin/config/system');
      const configData = response.data.config || {};
      
      setConfig(configData);
      form.setFieldsValue({
        odoo_url: configData.odoo_url || '',
      });
    } catch (error: any) {
      console.error('System config error:', error);
      message.error(error.response?.data?.message || 'Failed to load system configuration');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async () => {
    try {
      setSaving(true);
      const values = await form.validateFields();
      
      await api.put('/api/super-admin/config/odoo', {
        odoo_url: values.odoo_url,
      });
      
      message.success('Odoo configuration saved successfully');
      fetchSystemConfig();
    } catch (error: any) {
      if (error.errorFields) {
        return;
      }
      message.error(error.response?.data?.message || 'Failed to save configuration');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div style={{ padding: '24px' }}>
      <Title level={2}>
        <LinkOutlined /> System Configuration
      </Title>
      <Paragraph>
        Configure platform-wide settings including external integrations.
      </Paragraph>

      <Card
        title="Odoo Integration"
        extra={config?.updated_at && (
          <Text type="secondary" style={{ fontSize: 12 }}>
            Last updated: {new Date(config.updated_at).toLocaleString()}
          </Text>
        )}
        loading={loading}
      >
        <Alert
          message="Odoo Payment Portal Integration"
          description={
            <>
              When users click "Top-Up Now" on the billing page, they will be redirected to your Odoo
              instance for payment processing. The system will generate a secure JWT token for automatic
              login.
              <br /><br />
              <strong>Integration Flow:</strong>
              <ol style={{ marginTop: 8, marginBottom: 0 }}>
                <li>User clicks "Top-Up Now" button</li>
                <li>System generates JWT token with tenant details</li>
                <li>User is redirected to: <code>{'{odoo_url}'}/saas/auto_login?token={'{jwt}'}</code></li>
                <li>Odoo verifies token and logs in user automatically</li>
                <li>User completes payment on Odoo portal</li>
              </ol>
            </>
          }
          type="info"
          showIcon
          icon={<InfoCircleOutlined />}
          style={{ marginBottom: 24 }}
        />

        <Form
          form={form}
          layout="vertical"
          onFinish={handleSubmit}
        >
          <Form.Item
            label="Odoo URL"
            name="odoo_url"
            rules={[
              { required: true, message: 'Please enter your Odoo URL' },
              { type: 'url', message: 'Please enter a valid URL' },
              {
                pattern: /^https?:\/\/.+/,
                message: 'URL must start with http:// or https://',
              },
            ]}
            extra="Enter your Odoo instance URL (e.g., https://your-company.odoo.com). Do not include trailing slash."
          >
            <Input
              prefix={<LinkOutlined />}
              placeholder="https://your-company.odoo.com"
              size="large"
            />
          </Form.Item>

          <Divider />

          <Alert
            message="JWT Secret Configuration"
            description={
              <>
                The JWT secret key is stored securely in the backend environment variables 
                (<code>ODOO_JWT_SECRET</code>). This same secret must be configured in your Odoo 
                instance to verify the authentication tokens.
                <br /><br />
                <strong>Token contains:</strong>
                <ul style={{ marginTop: 8, marginBottom: 0 }}>
                  <li><code>tenant_id</code> - Client tenant identifier</li>
                  <li><code>email</code> - Original email used to create the tenant</li>
                  <li><code>name</code> - Company name</li>
                  <li><code>app</code> - Application identifier (texo)</li>
                  <li><code>exp</code> - Token expiration (10 minutes)</li>
                </ul>
              </>
            }
            type="warning"
            showIcon
            style={{ marginBottom: 24 }}
          />

          <Space>
            <Button
              type="primary"
              htmlType="submit"
              icon={<SaveOutlined />}
              loading={saving}
              size="large"
            >
              Save Configuration
            </Button>
            <Button onClick={fetchSystemConfig} disabled={loading || saving}>
              Reset
            </Button>
          </Space>
        </Form>
      </Card>
    </div>
  );
};

export default SystemConfigPage;
